<template>
    <div class="container py-5">

        <!-- Toolbar: busca + filtro de seção -->
        <div v-if="groups.length" class="nch-acervo-toolbar">
            <div class="nch-acervo-search">
                <i class="fas fa-search nch-acervo-search__icon"></i>
                <input
                    v-model="search"
                    class="nch-acervo-search__input"
                    type="text"
                    placeholder="Buscar arquivo ou pasta..."
                    autocomplete="off"
                />
                <button v-if="search" class="nch-acervo-search__clear" @click="search = ''">
                    <i class="mdi mdi-close"></i>
                </button>
            </div>

            <div v-if="groups.length > 1" class="nch-acervo-filters">
                <button
                    class="nch-acervo-filter-chip"
                    :class="{ 'nch-acervo-filter-chip--active': activeSection === null }"
                    @click="activeSection = null"
                >Todas</button>
                <button
                    v-for="group in groups"
                    :key="group.gallery_id"
                    class="nch-acervo-filter-chip"
                    :class="{ 'nch-acervo-filter-chip--active': activeSection === group.gallery_id }"
                    @click="activeSection = activeSection === group.gallery_id ? null : group.gallery_id"
                >
                    {{ group.gallery_name }}
                    <span class="nch-acervo-filter-count">{{ group.files.length }}</span>
                </button>
            </div>
        </div>

        <div v-if="!groups.length" class="text-center grey--text py-6">
            <i class="mdi mdi-folder-open-outline" style="font-size:48px;opacity:.3"></i>
            <p class="mt-2">Nenhum arquivo encontrado.</p>
        </div>
        <div v-else-if="filteredGroups.length === 0" class="text-center py-6" style="color:var(--page-text-secondary,#9e9e9e)">
            <i class="mdi mdi-magnify-remove-outline" style="font-size:48px;opacity:.4"></i>
            <p class="mt-2">Nenhum resultado para "<strong>{{ search }}</strong>"</p>
        </div>

        <template v-else>
            <div
                v-for="(group, gi) in filteredGroups"
                :key="group.gallery_id"
                class="nch-acervo-section"
                :class="{ 'nch-acervo-section--spaced': gi > 0 }"
            >
                <div v-if="(showTitles && groups.length > 1) || showDownloadAll" class="nch-acervo-section__header">
                    <div class="nch-acervo-section__heading">
                        <span v-if="showTitles && groups.length > 1" class="nch-acervo-section__title">{{ group.gallery_name }}</span>
                        <span class="nch-acervo-section__count">{{ group.files.length }} arquivo{{ group.files.length !== 1 ? 's' : '' }}</span>
                    </div>
                    <a
                        v-if="showDownloadAll && group.files.length"
                        class="nch-acervo-zip-btn"
                        :href="zipUrl(group.gallery_id)"
                    >
                        <i class="mdi mdi-folder-zip-outline" style="margin-right:6px"></i>
                        Baixar tudo
                    </a>
                </div>

                <ul class="nch-acervo-list">
                    <li
                        v-for="file in visibleFiles(group, gi)"
                        :key="file.id"
                        class="nch-acervo-item"
                    >
                        <span class="nch-acervo-item__icon" :class="iconClass(file)">
                            <i :class="fileIcon(file)"></i>
                        </span>
                        <div class="nch-acervo-item__info">
                            <span class="nch-acervo-item__name" :title="file.name">{{ file.name }}</span>
                            <span class="nch-acervo-item__meta">
                                <span v-if="file.ext" class="nch-acervo-item__ext">{{ file.ext.toUpperCase() }}</span>
                                <span v-if="file.created_at">{{ formatDate(file.created_at) }}</span>
                            </span>
                        </div>
                        <a
                            v-if="isViewable(file) && file.view_url"
                            class="nch-acervo-item__download nch-acervo-item__view"
                            :href="file.view_url"
                            target="_blank"
                            rel="noopener noreferrer"
                            :aria-label="'Visualizar ' + file.name"
                        >
                            <i class="mdi mdi-eye-outline"></i>
                            <span class="nch-acervo-item__download-label">Visualizar</span>
                        </a>
                        <a
                            class="nch-acervo-item__download"
                            :href="file.url"
                            target="_blank"
                            rel="noopener noreferrer"
                            :aria-label="'Baixar ' + file.name"
                        >
                            <i class="mdi mdi-download"></i>
                            <span class="nch-acervo-item__download-label">Baixar</span>
                        </a>
                    </li>
                </ul>

                <div v-if="hasMore(group, gi)" class="nch-acervo__load-more">
                    <button class="nch-acervo__load-btn" @click="loadMore(gi)">
                        Ver mais {{ remainingCount(group, gi) }} arquivo{{ remainingCount(group, gi) !== 1 ? 's' : '' }}
                    </button>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
export default {
    name: 'AcervoPage',

    props: {
        config: { type: Object, default: () => ({}) },
        files: { type: Array, default: () => [] },
        slug: { type: String, default: '' },
    },

    data() {
        return {
            search: '',
            activeSection: null,
            sectionLoaded: {},
        };
    },

    computed: {
        perPage() {
            return this.config.per_page || 20;
        },
        showTitles() {
            return this.config.show_section_titles !== false;
        },
        showDownloadAll() {
            return this.config.show_download_all !== false;
        },

        groups() {
            if (!this.files || !this.files.length) return [];
            if (this.files[0] && this.files[0].files) return this.files;
            return [{ gallery_id: 0, gallery_name: '', files: this.files }];
        },

        filteredGroups() {
            let result = this.groups;

            if (this.activeSection !== null) {
                result = result.filter(g => g.gallery_id === this.activeSection);
            }

            const q = this.search.trim().toLowerCase();
            if (!q) return result;

            return result
                .map(g => {
                    if (g.gallery_name && g.gallery_name.toLowerCase().includes(q)) return g;
                    const filtered = g.files.filter(f => (f.name || '').toLowerCase().includes(q));
                    if (filtered.length) return { ...g, files: filtered };
                    return null;
                })
                .filter(Boolean);
        },
    },

    methods: {
        visibleFiles(group, gi) {
            const pages = this.sectionLoaded[gi] || 1;
            return group.files.slice(0, pages * this.perPage);
        },
        hasMore(group, gi) {
            const pages = this.sectionLoaded[gi] || 1;
            return group.files.length > pages * this.perPage;
        },
        remainingCount(group, gi) {
            const pages = this.sectionLoaded[gi] || 1;
            const shown = pages * this.perPage;
            return Math.min(this.perPage, group.files.length - shown);
        },
        loadMore(gi) {
            this.sectionLoaded[gi] = (this.sectionLoaded[gi] || 1) + 1;
        },

        zipUrl(galleryId) {
            return `/p/${this.slug}/acervo/${galleryId}/zip`;
        },

        /**
         * Formatos que o navegador renderiza direto (botão Visualizar):
         * PDF, imagem, vídeo e áudio. SVG fica de fora (inline no mesmo
         * domínio executa scripts); demais formatos (doc/xls/ppt/zip/etc.)
         * seguem só com o Baixar.
         */
        isViewable(file) {
            if ((file.ext || '').toLowerCase() === 'svg') return false;
            return ['pdf', 'img', 'video', 'audio'].includes(this.fileGroup(file));
        },

        fileGroup(file) {
            const ext = (file.ext || '').toLowerCase();
            const mime = (file.mime || '').toLowerCase();
            if (['pdf'].includes(ext) || mime.includes('pdf')) return 'pdf';
            if (['doc', 'docx', 'odt', 'rtf'].includes(ext) || mime.includes('word')) return 'doc';
            if (['ppt', 'pptx', 'odp'].includes(ext) || mime.includes('presentation')) return 'ppt';
            if (['xls', 'xlsx', 'ods', 'csv'].includes(ext) || mime.includes('sheet') || mime.includes('excel')) return 'xls';
            if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return 'zip';
            if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp'].includes(ext) || mime.includes('image')) return 'img';
            if (['mp4', 'mov', 'avi', 'mkv', 'webm'].includes(ext) || mime.includes('video')) return 'video';
            if (['mp3', 'wav', 'ogg', 'm4a'].includes(ext) || mime.includes('audio')) return 'audio';
            return 'file';
        },
        fileIcon(file) {
            const map = {
                pdf: 'mdi mdi-file-pdf-box',
                doc: 'mdi mdi-file-word-box',
                ppt: 'mdi mdi-file-powerpoint-box',
                xls: 'mdi mdi-file-excel-box',
                zip: 'mdi mdi-folder-zip',
                img: 'mdi mdi-file-image',
                video: 'mdi mdi-file-video',
                audio: 'mdi mdi-file-music',
                file: 'mdi mdi-file-document-outline',
            };
            return map[this.fileGroup(file)];
        },
        iconClass(file) {
            return 'nch-acervo-item__icon--' + this.fileGroup(file);
        },
        formatDate(d) {
            if (!d) return '';
            const parts = String(d).split('-');
            if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
            return d;
        },
    },
};
</script>

<style scoped>
.nch-acervo-toolbar {
    padding: 16px 0 12px;
    border-bottom: 1px solid var(--page-border, rgba(0,0,0,0.08));
    margin-bottom: 24px;
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    align-items: center;
}

.nch-acervo-search {
    position: relative;
    display: flex;
    align-items: center;
    background: var(--page-surface, #f5f5f5);
    border: 1.5px solid var(--page-border, #e0e0e0);
    border-radius: 10px;
    padding: 0 12px;
    height: 44px;
    min-width: 200px;
    transition: border-color 0.18s, box-shadow 0.18s;
}
.nch-acervo-search:focus-within {
    border-color: var(--page-accent, #1976d2);
    box-shadow: 0 0 0 3px rgba(25,118,210,0.1);
}
.nch-acervo-search__icon { color: var(--page-text-secondary, #9e9e9e); font-size: .875rem; margin-right: 8px; flex-shrink: 0; }
.nch-acervo-search__input { background: transparent; border: none; outline: none; color: var(--page-text, #212121); font-size: .9rem; flex: 1; min-width: 0; }
.nch-acervo-search__input::placeholder { color: var(--page-text-secondary, #9e9e9e); }
.nch-acervo-search__clear { background: none; border: none; cursor: pointer; color: var(--page-text-secondary, #9e9e9e); padding: 0; margin-left: 4px; display: flex; align-items: center; font-size: 1rem; }
.nch-acervo-search__clear:hover { color: var(--page-text, #212121); }

.nch-acervo-filters { display: flex; flex-wrap: wrap; gap: 6px; flex: 1; min-width: 200px; }
.nch-acervo-filter-chip {
    padding: 6px 14px; border-radius: 99px;
    border: 1.5px solid var(--page-border, #e0e0e0);
    background: transparent; color: var(--page-text, #424242);
    font-size: .8rem; font-weight: 600; cursor: pointer;
    transition: background .15s, border-color .15s, color .15s;
    white-space: nowrap; display: flex; align-items: center; gap: 6px;
}
.nch-acervo-filter-chip:hover { border-color: var(--page-accent, #1976d2); color: var(--page-accent, #1976d2); }
.nch-acervo-filter-chip--active {
    background: var(--page-accent, #1976d2) !important;
    border-color: var(--page-accent, #1976d2) !important;
    color: var(--page-accent-text, #fff) !important;
}
.nch-acervo-filter-count { font-size: .7rem; opacity: .7; font-weight: 700; }

.nch-acervo-section--spaced { margin-top: 2.5rem; }
.nch-acervo-section__header {
    display: flex; align-items: center; justify-content: space-between;
    gap: 10px; margin-bottom: 12px; padding-bottom: 8px;
    border-bottom: 2px solid var(--page-border, #eeeeee);
}
.nch-acervo-section__heading { display: flex; align-items: baseline; gap: 10px; }
.nch-acervo-section__title { font-size: 1.05rem; font-weight: 700; color: var(--page-text, #212121); letter-spacing: -0.01em; }
.nch-acervo-section__count { font-size: .78rem; color: var(--page-text-secondary, #9e9e9e); }

.nch-acervo-zip-btn {
    display: inline-flex; align-items: center;
    background: var(--page-accent, #1976d2); color: var(--page-accent-text, #fff);
    border-radius: 8px; padding: 7px 14px; font-size: .8rem; font-weight: 600;
    text-decoration: none; transition: opacity .15s, transform .14s; white-space: nowrap;
}
.nch-acervo-zip-btn:hover { opacity: .9; transform: translateY(-1px); }

.nch-acervo-list { list-style: none; padding: 0; margin: 0; }
.nch-acervo-item {
    display: flex; align-items: center; gap: 14px;
    padding: 12px 14px; border: 1px solid var(--page-border, #eee);
    border-radius: 10px; margin-bottom: 8px;
    background: var(--page-surface, #fff);
    transition: border-color .15s, box-shadow .15s, transform .12s;
}
.nch-acervo-item:hover {
    border-color: var(--page-accent, #1976d2);
    box-shadow: 0 2px 10px rgba(0,0,0,.06);
}
.nch-acervo-item__icon {
    flex-shrink: 0; width: 40px; height: 40px; border-radius: 8px;
    display: flex; align-items: center; justify-content: center;
    font-size: 1.4rem; color: #fff;
}
.nch-acervo-item__icon--pdf { background: #e53935; }
.nch-acervo-item__icon--doc { background: #1e88e5; }
.nch-acervo-item__icon--ppt { background: #fb8c00; }
.nch-acervo-item__icon--xls { background: #43a047; }
.nch-acervo-item__icon--zip { background: #6d4c41; }
.nch-acervo-item__icon--img { background: #8e24aa; }
.nch-acervo-item__icon--video { background: #3949ab; }
.nch-acervo-item__icon--audio { background: #00897b; }
.nch-acervo-item__icon--file { background: #757575; }

.nch-acervo-item__info { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.nch-acervo-item__name {
    font-size: .92rem; font-weight: 600; color: var(--page-text, #212121);
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.nch-acervo-item__meta { display: flex; align-items: center; gap: 8px; font-size: .72rem; color: var(--page-text-secondary, #9e9e9e); }
.nch-acervo-item__ext { font-weight: 700; letter-spacing: .04em; }

.nch-acervo-item__download {
    flex-shrink: 0; display: inline-flex; align-items: center; gap: 6px;
    border: 1.5px solid var(--page-border, #e0e0e0); border-radius: 8px;
    padding: 7px 14px; font-size: .82rem; font-weight: 600;
    color: var(--page-text, #424242); text-decoration: none;
    transition: background .15s, border-color .15s, color .15s;
}
.nch-acervo-item__download:hover {
    background: var(--page-accent, #1976d2); border-color: var(--page-accent, #1976d2);
    color: var(--page-accent-text, #fff);
}

.nch-acervo__load-more { margin-top: 16px; text-align: center; }
.nch-acervo__load-btn {
    background: var(--page-surface, #fff); border: 1.5px solid var(--page-border, #e0e0e0);
    border-radius: 24px; padding: 9px 22px; font-size: .875rem; font-weight: 600;
    color: var(--page-text, #424242); cursor: pointer;
    transition: border-color .18s, box-shadow .18s, transform .14s;
}
.nch-acervo__load-btn:hover { border-color: var(--page-text-secondary, #9e9e9e); box-shadow: 0 2px 8px rgba(0,0,0,.08); transform: translateY(-1px); }

@media (max-width: 600px) {
    .nch-acervo-toolbar { flex-direction: column; align-items: stretch; }
    .nch-acervo-search, .nch-acervo-filters { width: 100%; }
    .nch-acervo-item__download-label { display: none; }
    .nch-acervo-section__header { flex-wrap: wrap; }
}
</style>
